import React, { useEffect, useState } from 'react';
import { getHeaders } from '../api';

// Compact single-row rendering of account-manager ownership. The big
// padded card it used to be was mostly empty space — reps glance here
// to see "who owns this" and occasionally change it. Single row with
// inline avatar + name + change-to-dropdown + save is enough.

const API = '';

export default function AccountManagerPanel({ accountId, account, onUpdated }) {
  const [staff, setStaff]       = useState([]);
  const [saving, setSaving]     = useState(false);
  const [editing, setEditing]   = useState(false);
  const [selected, setSelected] = useState('');
  const [message, setMessage]   = useState('');

  const currentManagerEmail = account?.Account_Manager_Email__c || null;
  const currentManagerName  = account?.Account_Manager_Name__c  || null;
  const isManaged           = account?.Is_Managed__c || false;

  useEffect(() => {
    fetch(`${API}/api/staff`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => {
        const list = d.staff || [];
        setStaff(list);
        if (currentManagerEmail) {
          const match = list.find(s => s.email === currentManagerEmail);
          if (match) setSelected(match.id);
        }
      })
      .catch(() => {});
  }, [currentManagerEmail]);

  async function handleAssign() {
    setSaving(true); setMessage('');
    try {
      const res = await fetch(`${API}/api/accounts/${accountId}/assign-manager`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ staffId: selected || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage(selected ? `✓ Assigned to ${data.manager.Account_Manager_Name__c}` : '✓ Manager removed');
      setEditing(false);
      if (onUpdated) onUpdated();
    } catch (err) {
      setMessage(`⚠ ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  const chipManaged   = { padding: '2px 8px', borderRadius: 4, background: 'rgba(74,222,128,0.15)', color: '#4ade80', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)', letterSpacing: 0.5 };
  const chipUnmanaged = { padding: '2px 8px', borderRadius: 4, background: 'rgba(107,114,128,0.2)',  color: '#9ca3af', fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)', letterSpacing: 0.5 };
  const textBtn       = { padding: '4px 10px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' };

  return (
    <div className="table-card" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Manager
      </span>

      {/* Current display */}
      {!editing && (
        <>
          {isManaged && currentManagerName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
              }}>{currentManagerName.charAt(0).toUpperCase()}</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{currentManagerName}</span>
                <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{currentManagerEmail || '—'}</span>
              </div>
            </div>
          ) : (
            <span style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>Unassigned</span>
          )}
          <span style={isManaged ? chipManaged : chipUnmanaged}>
            {isManaged ? 'MANAGED' : 'UNMANAGED'}
          </span>
          <span style={{ flex: 1 }} />
          <button data-write onClick={() => setEditing(true)} style={textBtn}>
            {currentManagerName ? 'Change' : 'Assign'}
          </button>
        </>
      )}

      {/* Edit state */}
      {editing && (
        <>
          <select
            value={selected}
            onChange={e => setSelected(e.target.value)}
            style={{
              flex: 1, minWidth: 200, background: 'var(--surface2)',
              border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px',
              color: 'var(--text)', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer',
            }}
          >
            <option value="">— Unassigned —</option>
            {staff.map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.profile ? ` — ${s.profile}` : ''}</option>
            ))}
          </select>
          <button onClick={handleAssign} disabled={saving} style={{
            padding: '6px 14px', background: 'var(--accent)', border: 'none', borderRadius: 6,
            color: '#fff', fontSize: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1, fontFamily: 'var(--font)',
          }}>{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={() => { setEditing(false); setMessage(''); }} style={textBtn}>Cancel</button>
        </>
      )}

      {message && !editing && (
        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: message.startsWith('⚠') ? 'var(--accent2)' : 'var(--accent3)' }}>
          {message}
        </span>
      )}
    </div>
  );
}
